// ═══════════════════════════════════════════════════════════════════════════════
//  PROPELLANT SAVINGS CALCULATOR — Full mission budget, all AOCS/ADCS operations
//  Default: apples-to-apples (same Isp both sides). Click any row for full detail.
// ═══════════════════════════════════════════════════════════════════════════════
import { useState } from "react";
import { useTheme, mono, Card, Label, Stat, Btn } from "./appHelpers";

const THRUSTER_OPTS = [
  { v: 70,  l: "Cold Gas 70s" },
  { v: 150, l: "Warm Gas 150s" },
  { v: 220, l: "Mono 220s ★" },
  { v: 300, l: "Mono 300s" },
];
const EP_HIGHER_ISP = [
  { v: 150, l: "Warm 150s" },
  { v: 300, l: "Mono 300s" },
  { v: 850, l: "EP 850s ★" },
];
const ION_ISP = 1500;

// ── Mini savings bar ──────────────────────────────────────────────────────────
function SavBar({ conv, ep, ion, maxVal }) {
  const C = useTheme();
  const max = maxVal || Math.max(conv, ep, ion ?? 0, 0.0001);
  const bar = (val, color, label) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 72, ...mono, fontSize: 8, color, textAlign: "right" }}>{label}</div>
      <div style={{ flex: 1, height: 6, background: C.eqBg, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${Math.min((Math.max(val, 0) / max) * 100, 100)}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <div style={{ width: 72, ...mono, fontSize: 9, color, textAlign: "right" }}>
        {val <= 0 ? "0.0000 kg" : val.toFixed(4) + " kg"}
      </div>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {bar(conv, "#ef4444", "CONV")}
      {bar(Math.max(ep, 0), "#22c55e", "EP-ADCS")}
      {ion != null && bar(Math.max(ion, 0), "#a855f7", "ION")}
    </div>
  );
}

// ── Three-column comparison cell ──────────────────────────────────────────────
function CmpRow({ label, conv, ep, ion, unit = "kg", fmt = v => v.toFixed(4), note }) {
  const C = useTheme();
  const best = Math.min(conv, ep, ion);
  const color = v => v === best ? "#22c55e" : v > best * 1.5 ? "#ef4444" : C.text;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 90px 90px",
      gap: 6, padding: "7px 0", borderBottom: `1px solid ${C.border}20` }}>
      <div>
        <div style={{ ...mono, fontSize: 9, color: C.text }}>{label}</div>
        {note && <div style={{ ...mono, fontSize: 8, color: C.muted }}>{note}</div>}
      </div>
      <div style={{ ...mono, fontSize: 10, color: "#ef4444", textAlign: "right", fontWeight: 600 }}>
        {fmt(conv)}{unit}
      </div>
      <div style={{ ...mono, fontSize: 10, color: "#22c55e", textAlign: "right", fontWeight: 600 }}>
        {fmt(ep)}{unit}
      </div>
      <div style={{ ...mono, fontSize: 10, color: color(ion), textAlign: "right", fontWeight: 600 }}>
        {fmt(ion)}{unit}
      </div>
    </div>
  );
}

// ── Operation detail panel (full-screen replacement when row clicked) ─────────
function OpDetail({ op, C, onBack }) {
  const sections = [
    { title: "What is this?", color: "#0e9cc0", body: op.detail.what },
    { title: `Conventional (Isp=${op.detail.ispConv}s) — why propellant is wasted`, color: "#ef4444", body: op.detail.conv_why },
    { title: "EP-ADCS — how it eliminates or reduces waste", color: "#22c55e", body: op.detail.ep_how },
    { title: "Ion Thruster — comparison with dead mass", color: "#a855f7", body: op.detail.ion_note },
  ];

  return (
    <div>
      <button onClick={onBack} style={{ ...mono, fontSize: 9, cursor: "pointer", padding: "6px 14px",
        background: "transparent", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 4,
        marginBottom: 14 }}>← BACK TO BREAKDOWN</button>

      {/* Header */}
      <Card color={op.catColor}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 20 }}>{op.icon}</span>
          <div>
            <div style={{ ...mono, fontSize: 13, color: C.text, fontWeight: 700 }}>{op.label}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              <span style={{ ...mono, fontSize: 7, padding: "1px 6px", borderRadius: 3,
                background: `${op.catColor}22`, border: `1px solid ${op.catColor}44`,
                color: op.catColor }}>{op.cat}</span>
              {op.zero_ep && <span style={{ ...mono, fontSize: 7, padding: "1px 6px", borderRadius: 3,
                background: "#22c55e22", border: "1px solid #22c55e44",
                color: "#22c55e" }}>ZERO PROP — EP-ADCS</span>}
            </div>
          </div>
        </div>
        <div style={{ ...mono, fontSize: 9, color: C.muted }}>{op.desc}</div>
      </Card>

      {/* Concept + mechanism cards */}
      {sections.map(s => (
        <Card key={s.title} color={s.color}>
          <Label color={s.color}>{s.title}</Label>
          {s.body.map((line, i) => (
            <div key={i} style={{ ...mono, fontSize: 10, color: line.startsWith("→") ? s.color : C.text,
              lineHeight: 1.9, paddingLeft: line.startsWith("→") ? 8 : 0 }}>{line}</div>
          ))}
        </Card>
      ))}

      {/* Three-way numbers */}
      <Card color="#0e9cc0">
        <Label>Propellant Comparison — Conv vs EP-ADCS vs Ion</Label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 90px 90px", gap: 6,
          paddingBottom: 8, borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
          <div style={{ ...mono, fontSize: 8, color: C.muted }}>ITEM</div>
          <div style={{ ...mono, fontSize: 8, color: "#ef4444", textAlign: "right" }}>CONV</div>
          <div style={{ ...mono, fontSize: 8, color: "#22c55e", textAlign: "right" }}>EP-ADCS</div>
          <div style={{ ...mono, fontSize: 8, color: "#a855f7", textAlign: "right" }}>ION (1500s)</div>
        </div>
        {op.detail.cmp.map((r, i) => (
          <CmpRow key={i} {...r} />
        ))}
      </Card>

      {/* Ion dead mass breakdown */}
      <Card color="#a855f7">
        <Label color="#a855f7">Ion Thruster — Full System Dead Mass (hardware overhead)</Label>
        <div style={{ ...mono, fontSize: 9, color: C.muted, lineHeight: 1.8, marginBottom: 12 }}>
          Ion thrusters require 150 W continuous power → solar panels + eclipse battery dominate mass budget on small satellites.
          Below {Math.round(op.detail.ionCrossover)} kg satellite mass the hardware overhead outweighs the propellant saving.
        </div>
        {op.detail.ionDeadMass.map((row, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between",
            padding: "6px 0", borderBottom: `1px solid ${C.border}20` }}>
            <span style={{ ...mono, fontSize: 9, color: C.text }}>{row[0]}</span>
            <span style={{ ...mono, fontSize: 9, color: row[2] || "#a855f7", fontWeight: 600 }}>{row[1]}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PropellantPage() {
  const C = useTheme();

  const [mass,        setMass]        = useState(8);
  const [alt,         setAlt]         = useState(425);
  const [thrusterIsp, setThrusterIsp] = useState(70);
  const [unlockIsp,   setUnlockIsp]   = useState(false);
  const [ispEPHigh,   setIspEPHigh]   = useState(850);
  const [tier,        setTier]        = useState("SKM4");
  const [years,       setYears]       = useState(5);
  const [phaseAngle,  setPhaseAngle]  = useState(180);
  const [phaseDays,   setPhaseDays]   = useState(30);
  const [dvDeorbit,   setDvDeorbit]   = useState(0);
  const [activeTab,   setActiveTab]   = useState("breakdown");
  const [selectedOp,  setSelectedOp]  = useState(null);

  const ispConv = thrusterIsp;
  const ispEP   = unlockIsp ? ispEPHigh : thrusterIsp;
  const isApples = ispConv === ispEP;

  // ── Physics ────────────────────────────────────────────────────────────────
  const G = 3.986e14, RE = 6371e3, g0 = 9.81;
  const rho   = 4.71e-13 * Math.exp((500 - alt) / 48);
  const v     = Math.sqrt(G / (RE + alt * 1000));
  const T_orb = 2 * Math.PI * Math.sqrt(Math.pow(RE + alt * 1000, 3) / G);
  const area  = mass <= 8 ? 0.02 : mass <= 25 ? 0.04 : 0.10;
  const Fd    = 0.5 * 2.2 * area * rho * v * v;
  const dv_sk    = Fd * years * 365.25 * 86400 / mass;
  const dv_phase = (2 * v * (phaseAngle * Math.PI / 180) * T_orb) /
                   (6 * Math.PI * phaseDays * 86400);
  const propM = (m0, dv, isp) => dv > 0 ? m0 * (1 - Math.exp(-dv / (isp * g0))) : 0;

  const DV_DESAT_YR = tier === "SKM4" ? 3.5 : 5.5;

  // Conv propellant
  const sk_c      = propM(mass, dv_sk, ispConv);
  const over_c    = sk_c * 0.639;
  const osc_c     = sk_c * 0.172;
  const desat_c   = propM(mass, DV_DESAT_YR * years, ispConv);
  const phase_c   = propM(mass, dv_phase, ispConv);
  const deorbit_c = propM(mass, dvDeorbit, ispConv);

  // EP-ADCS propellant
  const sk_e      = propM(mass, dv_sk, ispEP);
  const daynight_e = -(sk_e * 0.08);
  const total_e   = Math.max(0, sk_e + daynight_e);

  // Ion propellant (Isp=1500s, throttleable → less overshoot/osc, but desat same)
  const sk_ion      = propM(mass, dv_sk, ION_ISP);
  const over_ion    = sk_ion * 0.050;  // throttleable → ~5% overshoot only
  const osc_ion     = sk_ion * 0.030;  // can modulate thrust → 3%
  const desat_ion   = propM(mass, DV_DESAT_YR * years, ispConv); // desat uses small thruster at conv Isp
  const phase_ion   = propM(mass, dv_phase, ION_ISP);
  const deorbit_ion = propM(mass, dvDeorbit, ION_ISP);
  const total_ion_prop = sk_ion + over_ion + osc_ion + desat_ion + phase_ion + deorbit_ion;

  // Ion dead mass (consistent with DeadMassPage)
  const sf          = mass / 100;
  const ion_hw_thruster = 2.5 * sf;
  const ion_hw_ppu      = 2.5 * sf;
  const ion_hw_rw       = 1.2 * sf;
  const ion_hw_solar    = (150 / 10) * sf;   // 150W at 10 W/kg
  const ion_hw_battery  = 150 * 35 * 60 / 3600 / 200 * sf;
  const ion_hw_tank     = 1.5 * sf;
  const ion_dead_mass   = ion_hw_thruster + ion_hw_ppu + ion_hw_rw + ion_hw_solar + ion_hw_battery + ion_hw_tank;
  const ion_total_system = total_ion_prop + ion_dead_mass;

  // EP-ADCS dead mass (minimal hardware)
  const ep_hw = 0.08 * sf + 0.15 + 0.4 * Math.min(sf, 4) + 0.3 + 1.0 * sf + 0.4 * sf + 0.3 * sf;
  const ep_total_system = total_e + ep_hw;
  const ion_crossover = 350; // kg — below this EP-ADCS wins on system mass

  // ── OPS ────────────────────────────────────────────────────────────────────
  const catColor = { AOCS: "#0e9cc0", ADCS: "#a855f7", MISSION: "#f59e0b" };

  const deadMassRows = [
    ["Thruster + nozzle assembly",        `${ion_hw_thruster.toFixed(3)} kg`, "#a855f7"],
    ["Power Processing Unit (PPU)",        `${ion_hw_ppu.toFixed(3)} kg`,     "#a855f7"],
    ["Solar panels (150 W @ 10 W/kg)",    `${ion_hw_solar.toFixed(3)} kg`,    "#ef4444"],
    ["Eclipse battery bank",               `${ion_hw_battery.toFixed(3)} kg`, "#ef4444"],
    ["Reaction wheels (needed for attitude)",`${ion_hw_rw.toFixed(3)} kg`,   "#f59e0b"],
    ["Xenon tank + feed system",           `${ion_hw_tank.toFixed(3)} kg`,    "#a855f7"],
    ["Total ion hardware dead mass",       `${ion_dead_mass.toFixed(3)} kg`,  "#ef4444"],
    ["Total ion system (prop + hardware)", `${ion_total_system.toFixed(3)} kg`, "#ef4444"],
    ["EP-ADCS total system (prop + hardware)", `${ep_total_system.toFixed(3)} kg`, "#22c55e"],
  ];

  const OPS = [
    {
      cat: "AOCS", icon: "📡",
      label: "Station Keeping — drag ΔV",
      desc: isApples
        ? `${dv_sk.toFixed(2)} m/s over ${years} yr · both use Isp=${ispConv}s (apples-to-apples)`
        : `${dv_sk.toFixed(2)} m/s · Conv=${ispConv}s vs EP=${ispEP}s`,
      conv: sk_c, ep: sk_e, ion: sk_ion, zero_ep: false,
      ep_how: isApples ? `Same Isp=${ispConv}s — day-night sync gives 8% credit in ADCS row`
        : `Higher Isp (${ispEP}s vs ${ispConv}s) → ${((1 - sk_e / sk_c) * 100).toFixed(0)}% less prop`,
      detail: {
        ispConv, what: [
          "Every satellite in LEO loses altitude continuously due to atmospheric drag.",
          `At ${alt} km, air density is ${(rho * 1e13).toFixed(2)} × 10⁻¹³ kg/m³ — thin but enough to cause`,
          `${(Fd * 1e6).toFixed(1)} μN of drag on a ${mass} kg satellite with ${(area * 1e4).toFixed(0)} cm² frontal area.`,
          "The thruster must periodically fire a prograde (forward) impulse to maintain altitude.",
          `Over ${years} years this accumulates to ${dv_sk.toFixed(2)} m/s of ΔV — the irreducible minimum.`,
        ],
        conv_why: [
          "The base drag ΔV is physics — it cannot be avoided.",
          `With a ${ispConv}s Isp thruster: ${sk_c.toFixed(4)} kg propellant for pure station keeping.`,
          "However conventional systems add ~63.9% overshoot waste (row 2) and ~17.2% oscillatory waste (row 3).",
          "→ Real conventional SK propellant ≈ sk_base × 1.639 × 1.172 = sk_base × 1.921",
        ],
        ep_how: [
          isApples
            ? `EP-ADCS uses the same ${ispEP}s thruster — base SK propellant is identical.`
            : `EP-ADCS uses ${ispEP}s Isp → same ΔV needs ${((1 - sk_e / sk_c) * 100).toFixed(0)}% less prop.`,
          "The real EP-ADCS advantage here is EMMS: it eliminates overshoot and oscillatory burns entirely (rows 2 & 3).",
          "→ EP-ADCS total SK-related prop = sk_base only (no 1.921× multiplier).",
          "Additional day-night density sync gives 8% further credit on the base SK propellant.",
        ],
        ion_note: [
          `Ion thruster (Isp=${ION_ISP}s) needs only ${sk_ion.toFixed(4)} kg for the same ${dv_sk.toFixed(2)} m/s ΔV.`,
          "This is the ion's key advantage — but it pays for it in hardware:",
          `→ 150 W solar panels: ${ion_hw_solar.toFixed(2)} kg dead mass on this ${mass} kg satellite`,
          `→ Eclipse battery: ${ion_hw_battery.toFixed(2)} kg`,
          `→ PPU + reaction wheels: ${(ion_hw_ppu + ion_hw_rw).toFixed(2)} kg`,
          `Total ion hardware dead mass: ${ion_dead_mass.toFixed(2)} kg vs propellant saved: ${(sk_c - sk_ion).toFixed(4)} kg`,
          ion_dead_mass > (sk_c - sk_ion)
            ? `→ Dead mass (${ion_dead_mass.toFixed(2)} kg) EXCEEDS propellant saving (${(sk_c - sk_ion).toFixed(4)} kg) — ion LOSES at ${mass} kg.`
            : `→ Propellant saving exceeds dead mass at ${mass} kg.`,
        ],
        cmp: [
          { label: "SK propellant", conv: sk_c, ep: sk_e, ion: sk_ion, note: `Isp: Conv=${ispConv}s | EP=${ispEP}s | Ion=${ION_ISP}s` },
          { label: "Ion hardware dead mass", conv: 0, ep: 0, ion: ion_dead_mass, note: "Solar + battery + PPU + RW + tank", fmt: v => v > 0 ? v.toFixed(3) : "N/A " },
        ],
        ionDeadMass: deadMassRows, ionCrossover: ion_crossover,
      },
    },
    {
      cat: "AOCS", icon: "🔄",
      label: "In-track Overshoot",
      desc: `Bang-bang retro-correction burns (η_in=1.639 → +${(over_c / sk_c * 100).toFixed(0)}% of SK prop)`,
      conv: over_c, ep: 0, ion: over_ion, zero_ep: true,
      ep_how: "EMMS absorbs overshoot the instant thruster fires — zero retrograde burn",
      detail: {
        ispConv, what: [
          "A conventional thruster fires in discrete pulses (bang-bang control).",
          "Each pulse delivers more impulse than the exact drag debt requires — the satellite overshoots the target position.",
          "A second, retrograde (backwards) burn then corrects the overshoot — consuming extra propellant.",
          "This inefficiency factor is η_in = 1.639 → every 1 kg of needed SK propellant wastes 0.639 kg on correction.",
          "The root cause: you cannot un-burn propellant once it's fired.",
        ],
        conv_why: [
          `Overshoot waste = SK propellant × 0.639 = ${over_c.toFixed(4)} kg over ${years} years.`,
          "Cause: valve minimum open time (typ. 0.5–1 ms) deposits more impulse than the ideal 2.93 mNs debt.",
          "The retrograde correction fires at the same Isp — doubling the propellant cost per correction event.",
          "→ Cannot be reduced without a mechanism to absorb overshoot in real-time.",
        ],
        ep_how: [
          "EMMS (Electromagnetic Mass Shifter) is pre-charged and at full force BEFORE the thruster fires.",
          "The instant the thruster closes, EMMS drives its internal 1 kg mass forward, absorbing the exact overshoot impulse.",
          "→ No retrograde burn. No wasted propellant. Zero.",
          "The EMMS coil needs 20 ms to reach full current — this 20 ms gap is the ONLY residual inefficiency (η=1.020).",
          `→ EP-ADCS in-track overshoot propellant: 0.0000 kg (vs ${over_c.toFixed(4)} kg conventional)`,
        ],
        ion_note: [
          "Ion thrusters are throttleable — they can reduce thrust to near-zero for fine positioning.",
          `Typical minimum throttle ~10% → overshoot is ~5% of SK propellant = ${over_ion.toFixed(4)} kg.`,
          "Much better than bang-bang, but the thruster still cannot react instantaneously.",
          "→ Ion still wastes some propellant here; EP-ADCS with EMMS is the only true-zero solution.",
          "Note: ion's attitude control still relies on reaction wheels → no EMMS-equivalent.",
        ],
        cmp: [
          { label: "Overshoot propellant", conv: over_c, ep: 0, ion: over_ion,
            note: `Conv: η_in=1.639 (+63.9%) | EP: EMMS absorbs | Ion: throttleable (~5%)` },
          { label: "Saving vs conventional", conv: 0, ep: over_c, ion: over_c - over_ion,
            note: "higher = more saved", fmt: v => v.toFixed(4) },
        ],
        ionDeadMass: deadMassRows, ionCrossover: ion_crossover,
      },
    },
    {
      cat: "AOCS", icon: "〰️",
      label: "Oscillatory Perturbations",
      desc: `SRP, J2, atmospheric tides, micro-impacts (η_osc=1.172 → +${(osc_c / sk_c * 100).toFixed(0)}% of SK prop)`,
      conv: osc_c, ep: 0, ion: osc_ion, zero_ep: true,
      ep_how: "EMMS continuous wideband disturbance rejection — no correction burns",
      detail: {
        ispConv, what: [
          "Multiple continuous disturbance forces push the satellite off its ideal orbit:",
          "① Solar Radiation Pressure (SRP): photon momentum from sunlight (~5 μN on a 0.02 m² face)",
          "② J2 oblateness: Earth's equatorial bulge causes orbital plane precession",
          "③ Atmospheric density variation: density fluctuates ±30% with solar flux, day-night cycles",
          "④ Micro-meteorite impacts: random impulse events, mostly sub-μNs but cumulative",
          "Together these add η_osc=1.172 to conventional SK propellant use.",
        ],
        conv_why: [
          `Oscillatory correction burns = ${osc_c.toFixed(4)} kg over ${years} years.`,
          "Each disturbance event requires a detection window, then a corrective burn.",
          "The burn itself cannot be perfectly sized → further overshoot (compounding with η_in).",
          "→ Conventional systems must carry 17.2% more propellant just to handle perturbations.",
        ],
        ep_how: [
          "EMMS provides continuous, propellant-free disturbance rejection — no detection delay, no burn.",
          "The 1 kg internal mass can absorb impulses up to 800 mNs at 800 mN force.",
          "SRP (~5 μN), density variations (~μN), micro-impacts (sub-μNs) are all within EMMS range.",
          "The mass absorbs disturbances, the slow reset (10 mN) spreads the correction over 0.64 s — negligible orbital effect.",
          "→ Zero propellant consumed for oscillatory corrections. Saving: " + osc_c.toFixed(4) + " kg.",
        ],
        ion_note: [
          "Ion thrusters can modulate thrust to counteract some perturbations (vary throttle 5–100%).",
          `Estimated residual oscillatory overhead ≈ 3% of ion SK = ${osc_ion.toFixed(4)} kg.`,
          "Attitude perturbations (SRP torques) still handled by reaction wheels — reaction wheel desat burns still needed.",
          "→ Ion is better than conventional but cannot match EMMS zero-propellant rejection.",
        ],
        cmp: [
          { label: "Oscillatory correction propellant", conv: osc_c, ep: 0, ion: osc_ion,
            note: "Conv: +17.2% | EP: EMMS absorbs → 0 | Ion: throttle modulation → ~3%" },
        ],
        ionDeadMass: deadMassRows, ionCrossover: ion_crossover,
      },
    },
    {
      cat: "ADCS", icon: "🌀",
      label: "Momentum Wheel Desaturation",
      desc: `RW dumps · ${DV_DESAT_YR} m/s/yr ΔV → ${(desat_c / years * 1000).toFixed(1)} g/yr @ Isp=${ispConv}s`,
      conv: desat_c, ep: 0, ion: desat_ion, zero_ep: true,
      ep_how: "Reaction wheels completely eliminated — EMMS is the sole attitude actuator",
      detail: {
        ispConv, what: [
          "Reaction wheels absorb external torques (SRP, gravity gradient, magnetic field) by spinning up.",
          "They have a maximum speed (rpm limit) — when reached the wheel is 'saturated'.",
          "Desaturation: fire thrusters to apply an equal-and-opposite torque while the wheel spins down.",
          "This is unavoidable physics — external torques continuously pump angular momentum into the wheels.",
          `Typical LEO desaturation ΔV: ${DV_DESAT_YR} m/s/yr (${tier} tier). Over ${years} yr: ${(DV_DESAT_YR * years).toFixed(1)} m/s.`,
        ],
        conv_why: [
          `Desat propellant = ${desat_c.toFixed(4)} kg over ${years} years.`,
          `Physical: ${DV_DESAT_YR} m/s/yr ΔV → rocket equation at Isp=${ispConv}s.`,
          "Cannot be eliminated without removing reaction wheels entirely.",
          "→ Historical missions: Kepler, Hayabusa, GALEX — all lost to reaction wheel failure/saturation.",
        ],
        ep_how: [
          "EMMS replaces reaction wheels entirely as the attitude actuator.",
          "EMMS absorbs external torques by translating its internal mass — no angular momentum accumulation.",
          "No spinning components → no saturation limit → no desaturation burns ever.",
          `→ Zero propellant for desaturation. Saving: ${desat_c.toFixed(4)} kg over ${years} years.`,
          "Bonus: no bearing wear, no lubrication degradation, no single-point failure mode.",
        ],
        ion_note: [
          "Ion thruster missions still use reaction wheels for fine attitude control.",
          "Ion thrusters are too powerful / slow for the micro-corrections attitude control needs.",
          `Desat ΔV is the same physics: ${DV_DESAT_YR} m/s/yr. Desat burns use a small conventional thruster.`,
          `Ion desat propellant: ${desat_ion.toFixed(4)} kg — identical to conventional.`,
          "→ Ion has NO advantage on desaturation. This is a pure EP-ADCS win.",
        ],
        cmp: [
          { label: "Desat propellant", conv: desat_c, ep: 0, ion: desat_ion,
            note: `${DV_DESAT_YR} m/s/yr × ${years} yr. Ion uses small thruster at Isp=${ispConv}s for desat.` },
          { label: "Reaction wheels needed", conv: 1, ep: 0, ion: 1,
            unit: "", fmt: v => v === 1 ? "YES" : "NO", note: "EMMS eliminates RW requirement" },
        ],
        ionDeadMass: deadMassRows, ionCrossover: ion_crossover,
      },
    },
    {
      cat: "ADCS", icon: "🌙",
      label: "Day-Night Density Sync",
      desc: "SSO: night-side atmosphere ~12% denser — EP-ADCS times burns to night side",
      conv: 0, ep: sk_e * -0.08, ion: 0, zero_ep: false, isSaving: true,
      ep_how: `Synchronises prograde burns to denser night pass → 8% saving on SK prop = ${(sk_e * 0.08).toFixed(4)} kg`,
      detail: {
        ispConv, what: [
          "In Sun-Synchronous Orbit (SSO), the satellite passes through two distinct atmospheric regions per orbit:",
          "① Day side: solar UV heats the atmosphere → it expands → lower density at altitude",
          "② Night side: no solar heating → atmosphere contracts → higher density",
          "The density difference is ~12% between day and night at 425 km.",
          "Higher density = more drag per pass = more efficient deceleration for a deorbit, but for SK:",
          "→ Firing the thruster when drag is highest means each gram of propellant replenishes more orbital energy.",
        ],
        conv_why: [
          "Conventional system fires based on position error threshold — whenever drift exceeds 31 mm.",
          "No awareness of instantaneous atmospheric density — fires equally on day and night passes.",
          "→ 0% day-night sync benefit. No credit.",
        ],
        ep_how: [
          "EP-ADCS flight computer reads orbital phase and density model in real-time.",
          "Deliberately delays the thruster fire event (within the EMMS absorption window) to align with the night pass.",
          "Each gram of propellant fired on the denser night side restores ~8% more orbital energy.",
          `→ Net saving: 8% × SK propellant = ${(sk_e * 0.08).toFixed(4)} kg over ${years} years.`,
          "This is a pure timing advantage — no hardware change, just smarter scheduling.",
        ],
        ion_note: [
          "Ion thrusters run continuously or in long bursts — they cannot easily sync to a 47-min night window.",
          "The minimum on-time for a Hall thruster is typically 5–30 min (warm-up, plasma equilibrium).",
          "→ Ion thrusters receive minimal day-night sync benefit. Effectively 0% credit.",
          "EP-ADCS pulse duration is 0.6 ms — trivial to schedule within any orbital window.",
        ],
        cmp: [
          { label: "Day-night sync credit", conv: 0, ep: sk_e * 0.08, ion: 0,
            note: "Positive = propellant saved. Conv & ion: cannot sync sub-ms pulses to orbital phase." },
        ],
        ionDeadMass: deadMassRows, ionCrossover: ion_crossover,
      },
    },
    {
      cat: "MISSION", icon: "🛰️",
      label: "Constellation Phasing",
      desc: `${phaseAngle}° deployment in ${phaseDays} days · Hohmann drift ΔV = ${dv_phase.toFixed(2)} m/s`,
      conv: phase_c, ep: 0, ion: phase_ion, zero_ep: true,
      ep_how: "Differential Cd modulation (face-on Cd≈3.0 / edge-on Cd≈1.5) — any angle, 0 prop",
      detail: {
        ispConv, what: [
          "A satellite constellation requires each satellite to occupy a specific orbital slot (phase angle).",
          "After launch, satellites are typically co-located — they must drift to their assigned positions.",
          "Method: slightly raise or lower the orbit (Hohmann transfer) → different orbital period → slow drift to target.",
          `Target: ${phaseAngle}° phase offset in ${phaseDays} days.`,
          `Required ΔV: ${dv_phase.toFixed(2)} m/s (two burns: raise + re-circularise).`,
        ],
        conv_why: [
          `Phasing propellant at Isp=${ispConv}s: ${phase_c.toFixed(4)} kg.`,
          "Standard Hohmann drift: fire prograde to raise apogee, drift, fire retrograde to re-circularise.",
          "Both burns require propellant — cannot be avoided with conventional propulsion.",
          "For large constellations with many planes/slots, phasing propellant is a significant budget item.",
        ],
        ep_how: [
          "EP-ADCS uses differential drag — changing the satellite's aerodynamic profile:",
          "→ Face-on (Cd≈3.0): maximum drag → orbit decays faster → satellite drifts backward",
          "→ Edge-on (Cd≈1.5): minimum drag → orbit decays slower → satellite moves forward relative to face-on",
          "By switching between profiles, any phase offset can be achieved — 0 propellant consumed.",
          `→ ${phaseAngle}° in ${phaseDays} days using drag modulation: 0.0000 kg prop. Saving: ${phase_c.toFixed(4)} kg.`,
        ],
        ion_note: [
          `Ion thruster phasing at Isp=${ION_ISP}s: ${phase_ion.toFixed(4)} kg — much less than conventional.`,
          "But phasing still consumes xenon propellant, and the 150W power demand during phasing must be sustained.",
          "→ Ion uses propellant for phasing; EP-ADCS uses zero.",
          `Saving vs ion: ${Math.max(0, phase_ion - 0).toFixed(4)} kg additional propellant freed by EP-ADCS.`,
        ],
        cmp: [
          { label: "Phasing propellant", conv: phase_c, ep: 0, ion: phase_ion,
            note: `${phaseAngle}° in ${phaseDays} days · EP: differential drag, 0 prop` },
        ],
        ionDeadMass: deadMassRows, ionCrossover: ion_crossover,
      },
    },
    {
      cat: "MISSION", icon: "🌍",
      label: "Deorbit / EOL Disposal",
      desc: `IADC <25yr rule · ΔV = ${dvDeorbit} m/s (0 = natural decay at ${alt} km)`,
      conv: deorbit_c, ep: 0, ion: deorbit_ion, zero_ep: true,
      ep_how: "Sustained differential drag gradually lowers apogee → passive atmospheric re-entry",
      detail: {
        ispConv, what: [
          "IADC guidelines require satellites below 2000 km to re-enter within 25 years of EOL.",
          `At ${alt} km, natural decay time ≈ ${alt < 450 ? "< 10" : alt < 500 ? "10–25" : "> 25"} years.`,
          dvDeorbit > 0
            ? `Active deorbit: ${dvDeorbit} m/s ΔV to lower perigee for re-entry within 25 years.`
            : "Natural decay is sufficient at this altitude — 0 m/s active ΔV needed.",
          "Active deorbit burns consume EOL propellant reserve — mass that must be launched.",
        ],
        conv_why: [
          dvDeorbit > 0
            ? `Deorbit propellant at Isp=${ispConv}s: ${deorbit_c.toFixed(4)} kg.`
            : "No active deorbit needed at this altitude — 0 kg conventional deorbit propellant.",
          "If active deorbit is required, the satellite must reserve propellant throughout the mission.",
          "This reduces payload mass margin for the entire mission life.",
        ],
        ep_how: [
          "EP-ADCS maximises drag continuously (face-on Cd≈3.0) after payload operations cease.",
          "This raises the effective drag force, accelerating natural orbital decay.",
          "→ The satellite naturally deorbits faster — no propellant consumed for disposal.",
          dvDeorbit > 0
            ? `→ Zero deorbit propellant. Saving: ${deorbit_c.toFixed(4)} kg released for payload/extended mission.`
            : "→ Zero deorbit propellant (same as conventional at this altitude — both use natural decay).",
        ],
        ion_note: [
          dvDeorbit > 0
            ? `Ion deorbit at Isp=${ION_ISP}s: ${deorbit_ion.toFixed(4)} kg — less than conventional.`
            : "No active deorbit needed — same as conventional.",
          "Ion can perform precise low-thrust deorbit spirals — efficient but still consumes xenon.",
          "EP-ADCS passive approach uses zero propellant — advantage applies whenever active ΔV is needed.",
        ],
        cmp: [
          { label: "Deorbit propellant", conv: deorbit_c, ep: 0, ion: deorbit_ion,
            note: `ΔV=${dvDeorbit} m/s · EP: passive differential drag` },
        ],
        ionDeadMass: deadMassRows, ionCrossover: ion_crossover,
      },
    },
  ].map(op => ({ ...op, catColor: catColor[op.cat] }));

  // ── Totals ─────────────────────────────────────────────────────────────────
  const total_c      = sk_c + over_c + osc_c + desat_c + phase_c + deorbit_c;
  const total_e_full = Math.max(0, sk_e - sk_e * 0.08);
  const total_ion    = total_ion_prop;
  const saved_kg     = total_c - total_e_full;
  const saved_pct    = total_c > 0 ? (saved_kg / total_c * 100) : 0;
  const barMax       = total_c;

  // ── Detail view ─────────────────────────────────────────────────────────────
  if (selectedOp !== null) {
    return <OpDetail op={OPS[selectedOp]} C={C} onBack={() => setSelectedOp(null)} />;
  }

  // ── Main view ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Apples-to-apples banner */}
      <div style={{
        background: isApples ? "#22c55e10" : "#f59e0b10",
        border: `1px solid ${isApples ? "#22c55e33" : "#f59e0b33"}`,
        borderLeft: `3px solid ${isApples ? "#22c55e" : "#f59e0b"}`,
        borderRadius: "0 6px 6px 0", padding: "8px 14px", marginBottom: 12,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
      }}>
        <div>
          <span style={{ ...mono, fontSize: 10, fontWeight: 700,
            color: isApples ? "#22c55e" : "#f59e0b" }}>
            {isApples ? "✓ APPLES-TO-APPLES MODE" : "⚡ ISP ADVANTAGE MODE"}
          </span>
          <span style={{ ...mono, fontSize: 9, color: C.muted, marginLeft: 10 }}>
            {isApples
              ? `Both Conv and EP-ADCS use Isp=${ispConv}s — savings are purely from EMMS mechanisms`
              : `Conv Isp=${ispConv}s vs EP-ADCS Isp=${ispEP}s — SK saving includes Isp benefit`}
          </span>
        </div>
        <button onClick={() => setUnlockIsp(u => !u)} style={{
          ...mono, fontSize: 8, padding: "4px 10px", cursor: "pointer", borderRadius: 4,
          background: unlockIsp ? "#f59e0b20" : "#22c55e10",
          border: `1px solid ${unlockIsp ? "#f59e0b66" : "#22c55e44"}`,
          color: unlockIsp ? "#f59e0b" : "#22c55e",
        }}>{unlockIsp ? "LOCK ISP (apples-to-apples)" : "UNLOCK ISP ADVANTAGE →"}</button>
      </div>

      {/* Parameters */}
      <Card color="#22c55e">
        <Label>Mission Parameters</Label>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>SATELLITE MASS</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {[4, 8, 14, 25, 50, 100].map(m => (
                <Btn key={m} active={mass === m} color="#22c55e" onClick={() => setMass(m)}>{m}kg</Btn>
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>ALTITUDE</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[400, 425, 450, 500].map(a => (
                <Btn key={a} active={alt === a} color="#22c55e" onClick={() => setAlt(a)}>{a}km</Btn>
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>MISSION LIFE</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[2, 3, 5, 7, 10].map(y => (
                <Btn key={y} active={years === y} color="#22c55e" onClick={() => setYears(y)}>{y}yr</Btn>
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>
              THRUSTER (both sides use same Isp)
              <span style={{ color: "#22c55e", marginLeft: 6 }}>← apples-to-apples default</span>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {THRUSTER_OPTS.map(t => (
                <Btn key={t.v} active={thrusterIsp === t.v} color="#0e9cc0"
                  onClick={() => setThrusterIsp(t.v)}>{t.l}</Btn>
              ))}
            </div>
          </div>
          {unlockIsp && (
            <div>
              <div style={{ ...mono, fontSize: 9, color: "#f59e0b", marginBottom: 6 }}>
                EP-ADCS ISP (higher) <span style={{ color: C.muted }}>← unlocked</span>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {EP_HIGHER_ISP.filter(t => t.v > thrusterIsp).map(t => (
                  <Btn key={t.v} active={ispEPHigh === t.v} color="#f59e0b"
                    onClick={() => setIspEPHigh(t.v)}>{t.l}</Btn>
                ))}
              </div>
            </div>
          )}
          <div>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>DESAT TIER</div>
            <div style={{ display: "flex", gap: 4 }}>
              <Btn active={tier === "SKM1"} color="#22c55e" onClick={() => setTier("SKM1")}>SKM-1</Btn>
              <Btn active={tier === "SKM4"} color="#22c55e" onClick={() => setTier("SKM4")}>SKM-4</Btn>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 14,
          paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          <div>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>CONSTELLATION PHASE TARGET</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[45, 90, 120, 180, 270, 360].map(d => (
                <Btn key={d} active={phaseAngle === d} color="#f59e0b" onClick={() => setPhaseAngle(d)}>{d}°</Btn>
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>PHASING TIME BUDGET</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[7, 14, 30, 60, 90].map(d => (
                <Btn key={d} active={phaseDays === d} color="#f59e0b" onClick={() => setPhaseDays(d)}>{d}d</Btn>
              ))}
            </div>
          </div>
          <div>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>DEORBIT ΔV</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[{ v: 0, l: "0 m/s" }, { v: 10, l: "10 m/s" }, { v: 25, l: "25 m/s" }, { v: 50, l: "50 m/s" }].map(d => (
                <Btn key={d.v} active={dvDeorbit === d.v} color="#a855f7" onClick={() => setDvDeorbit(d.v)}>{d.l}</Btn>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Headline stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
        <Stat label={`Conv total (Isp=${ispConv}s)`} value={total_c.toFixed(3)} unit="kg"
          color="#ef4444" sub={`${(total_c / mass * 100).toFixed(1)}% of sat mass`} />
        <Stat label={`EP-ADCS total (Isp=${ispEP}s)`} value={total_e_full.toFixed(3)} unit="kg"
          color="#22c55e" sub={`${(total_e_full / mass * 100).toFixed(1)}% of sat mass`} />
        <Stat label="Ion prop only (Isp=1500s)" value={total_ion.toFixed(3)} unit="kg"
          color="#a855f7" sub={`+${ion_dead_mass.toFixed(2)} kg dead mass`} />
        <Stat label="EMMS mechanism saving" value={saved_pct.toFixed(1)} unit="%"
          color="#0e9cc0" sub={`${saved_kg.toFixed(3)} kg vs Conv`} />
        <Stat label="SK ΔV" value={dv_sk.toFixed(2)} unit="m/s"
          color={C.text} sub={`${(dv_sk / years).toFixed(2)} m/s/yr`} />
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
        {[["breakdown","Full Breakdown"],["bars","Savings Bars"],["ion","Ion Comparison"],["impact","Mission Impact"]].map(([id,lbl]) => (
          <Btn key={id} active={activeTab === id} color="#22c55e" onClick={() => setActiveTab(id)}>{lbl}</Btn>
        ))}
      </div>

      {/* ══ FULL BREAKDOWN ══ */}
      {activeTab === "breakdown" && (
        <Card color="#22c55e">
          <Label>All Operations — click any row for full detail + ion comparison</Label>
          <div style={{ ...mono, fontSize: 8, color: C.muted, marginBottom: 10 }}>
            Click a row → concept explanation, mechanism breakdown, ion thruster comparison with dead mass accounting
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "24px 1fr 90px 90px 90px 72px",
            gap: 6, paddingBottom: 8, borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
            <div />
            <div style={{ ...mono, fontSize: 8, color: C.muted }}>OPERATION / MECHANISM</div>
            <div style={{ ...mono, fontSize: 8, color: "#ef4444", textAlign: "right" }}>CONV ({ispConv}s)</div>
            <div style={{ ...mono, fontSize: 8, color: "#22c55e", textAlign: "right" }}>EP-ADCS ({ispEP}s)</div>
            <div style={{ ...mono, fontSize: 8, color: "#0e9cc0", textAlign: "right" }}>SAVING</div>
            <div style={{ ...mono, fontSize: 8, color: "#f59e0b", textAlign: "right" }}>%</div>
          </div>

          {OPS.map((op, i) => {
            const saving = op.isSaving ? Math.abs(op.ep) : op.conv - Math.max(op.ep, 0);
            const savPct = op.isSaving ? "bonus"
              : op.conv > 0 ? (saving / op.conv * 100).toFixed(0) + "%"
              : "—";
            const savColor = (op.zero_ep || op.isSaving) ? "#22c55e"
              : saving > 0 ? "#0e9cc0" : C.muted;
            return (
              <div key={i}
                onClick={() => setSelectedOp(i)}
                style={{ marginBottom: 10, paddingBottom: 10,
                  borderBottom: i < OPS.length - 1 ? `1px solid ${C.border}` : "none",
                  cursor: "pointer", borderRadius: 6, padding: "8px",
                  transition: "background 0.1s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = `${op.catColor}08`}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>

                <div style={{ display: "grid", gridTemplateColumns: "24px 1fr 90px 90px 90px 72px",
                  gap: 6, alignItems: "flex-start" }}>
                  <div style={{ ...mono, fontSize: 14, lineHeight: 1 }}>{op.icon}</div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.text }}>{op.label}</span>
                      <span style={{ ...mono, fontSize: 7, padding: "1px 5px", borderRadius: 3,
                        background: `${op.catColor}22`, border: `1px solid ${op.catColor}44`,
                        color: op.catColor }}>{op.cat}</span>
                      {op.zero_ep && <span style={{ ...mono, fontSize: 7, padding: "1px 5px", borderRadius: 3,
                        background: "#22c55e22", border: "1px solid #22c55e44",
                        color: "#22c55e" }}>ZERO PROP</span>}
                      <span style={{ ...mono, fontSize: 7, color: C.muted, marginLeft: "auto" }}>tap for detail →</span>
                    </div>
                    <div style={{ ...mono, fontSize: 9, color: C.muted }}>{op.desc}</div>
                    <div style={{ fontSize: 9, color: "#22c55e", marginTop: 2 }}>↳ {op.ep_how}</div>
                  </div>
                  <div style={{ ...mono, fontSize: 11, color: "#ef4444", textAlign: "right", fontWeight: 600 }}>
                    {op.isSaving ? "—" : op.conv > 0 ? op.conv.toFixed(4) + " kg" : "0.0000 kg"}
                  </div>
                  <div style={{ ...mono, fontSize: 11, color: "#22c55e", textAlign: "right", fontWeight: 600 }}>
                    {op.isSaving ? `−${Math.abs(op.ep).toFixed(4)} kg` : Math.max(op.ep, 0).toFixed(4) + " kg"}
                  </div>
                  <div style={{ ...mono, fontSize: 11, color: savColor, textAlign: "right", fontWeight: 600 }}>
                    {op.isSaving ? `+${Math.abs(op.ep).toFixed(4)} kg`
                      : saving > 0 ? saving.toFixed(4) + " kg" : "0.0000 kg"}
                  </div>
                  <div style={{ ...mono, fontSize: 11, color: savColor, textAlign: "right", fontWeight: 700 }}>
                    {savPct}
                  </div>
                </div>
                {!op.isSaving && (
                  <div style={{ paddingLeft: 30, marginTop: 6 }}>
                    <SavBar conv={op.conv} ep={Math.max(op.ep, 0)} ion={op.ion} maxVal={barMax} />
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ borderTop: `2px solid ${C.border}`, paddingTop: 10,
            display: "grid", gridTemplateColumns: "24px 1fr 90px 90px 90px 72px", gap: 6 }}>
            <div />
            <div style={{ ...mono, fontSize: 10, color: C.text, fontWeight: 700 }}>TOTAL PROPELLANT</div>
            <div style={{ ...mono, fontSize: 13, color: "#ef4444", textAlign: "right", fontWeight: 700 }}>{total_c.toFixed(4)} kg</div>
            <div style={{ ...mono, fontSize: 13, color: "#22c55e", textAlign: "right", fontWeight: 700 }}>{total_e_full.toFixed(4)} kg</div>
            <div style={{ ...mono, fontSize: 13, color: "#0e9cc0", textAlign: "right", fontWeight: 700 }}>{saved_kg.toFixed(4)} kg</div>
            <div style={{ ...mono, fontSize: 13, color: "#0e9cc0", textAlign: "right", fontWeight: 700 }}>{saved_pct.toFixed(1)}%</div>
          </div>
        </Card>
      )}

      {/* ══ SAVINGS BARS ══ */}
      {activeTab === "bars" && (
        <Card color="#0e9cc0">
          <Label>Propellant per Operation — Conv (red) · EP-ADCS (green) · Ion (purple)</Label>
          {OPS.filter(op => !op.isSaving).map((op, i) => {
            const saving = op.conv - Math.max(op.ep, 0);
            const savPct = op.conv > 0 ? (saving / op.conv * 100) : (op.ep === 0 ? 100 : 0);
            return (
              <div key={i} style={{ marginBottom: 18, cursor: "pointer" }} onClick={() => setSelectedOp(OPS.indexOf(op))}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 10, color: C.text }}>{op.icon} {op.label}</span>
                  <span style={{ ...mono, fontSize: 9, padding: "2px 7px", borderRadius: 3,
                    background: savPct >= 99 ? "#22c55e22" : savPct >= 30 ? "#0e9cc022" : "#f59e0b22",
                    border: `1px solid ${savPct >= 99 ? "#22c55e44" : savPct >= 30 ? "#0e9cc044" : "#f59e0b44"}`,
                    color: savPct >= 99 ? "#22c55e" : savPct >= 30 ? "#0e9cc0" : "#f59e0b" }}>
                    {savPct >= 99.9 ? "100% saved" : `${savPct.toFixed(0)}% saved`}
                  </span>
                </div>
                <SavBar conv={op.conv} ep={Math.max(op.ep, 0)} ion={op.ion} maxVal={barMax} />
              </div>
            );
          })}
          <div style={{ paddingTop: 12, borderTop: `2px solid ${C.border}` }}>
            <div style={{ ...mono, fontSize: 9, color: C.muted, marginBottom: 6 }}>TOTAL — {saved_pct.toFixed(1)}% SAVED vs CONVENTIONAL</div>
            <SavBar conv={total_c} ep={total_e_full} ion={total_ion} maxVal={total_c} />
          </div>
        </Card>
      )}

      {/* ══ ION COMPARISON ══ */}
      {activeTab === "ion" && (
        <>
          <Card color="#a855f7">
            <Label color="#a855f7">Ion Thruster vs EP-ADCS — Full System Comparison</Label>
            <div style={{ ...mono, fontSize: 9, color: C.muted, lineHeight: 1.8, marginBottom: 12 }}>
              Ion thrusters save propellant via high Isp (1500s) but require 150W continuous power → large solar panels + battery.
              For satellites below ~{ion_crossover} kg, the hardware dead mass outweighs the propellant saving.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 90px 90px", gap: 6,
              paddingBottom: 8, borderBottom: `1px solid ${C.border}`, marginBottom: 8 }}>
              <div style={{ ...mono, fontSize: 8, color: C.muted }}>ITEM</div>
              <div style={{ ...mono, fontSize: 8, color: "#ef4444", textAlign: "right" }}>CONV ({ispConv}s)</div>
              <div style={{ ...mono, fontSize: 8, color: "#22c55e", textAlign: "right" }}>EP-ADCS ({ispEP}s)</div>
              <div style={{ ...mono, fontSize: 8, color: "#a855f7", textAlign: "right" }}>ION (1500s)</div>
            </div>

            {[
              ["Station Keeping prop",       sk_c,      sk_e,           sk_ion],
              ["Overshoot prop",             over_c,    0,              over_ion],
              ["Oscillatory prop",           osc_c,     0,              osc_ion],
              ["Desat prop",                 desat_c,   0,              desat_ion],
              ["Phasing prop",               phase_c,   0,              phase_ion],
              ["Deorbit prop",               deorbit_c, 0,              deorbit_ion],
              ["TOTAL PROPELLANT",           total_c,   total_e_full,   total_ion],
            ].map(([label, c, e, ion], i) => (
              <CmpRow key={i} label={label} conv={c} ep={e} ion={ion}
                note={i === 6 ? "pure propellant, before hardware" : undefined} />
            ))}

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: `2px solid ${C.border}` }}>
              <div style={{ ...mono, fontSize: 9, color: "#a855f7", marginBottom: 8, letterSpacing: 2 }}>
                ION HARDWARE DEAD MASS (added overhead)
              </div>
              {[
                ["Thruster + PPU",           ion_hw_thruster + ion_hw_ppu, 0, 0],
                ["Solar panels (150 W)",     ion_hw_solar,                 0, 0],
                ["Eclipse battery",          ion_hw_battery,               0, 0],
                ["Reaction wheels",          ion_hw_rw,                    0, 0],
                ["Xenon tank + feed",        ion_hw_tank,                  0, 0],
                ["Total hardware dead mass", ion_dead_mass,                0, 0],
              ].map(([label, c], i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 90px 90px 90px",
                  gap: 6, padding: "5px 0", borderBottom: `1px solid ${C.border}20` }}>
                  <div style={{ ...mono, fontSize: 9, color: C.text }}>{label}</div>
                  <div style={{ ...mono, fontSize: 9, color: "#ef4444", textAlign: "right" }}>
                    {c.toFixed(3)} kg
                  </div>
                  <div style={{ ...mono, fontSize: 9, color: "#22c55e", textAlign: "right" }}>
                    {label.includes("solar") || label.includes("battery") || label.includes("PPU") ? "not needed" :
                     label.includes("wheel") ? "eliminated" : "—"}
                  </div>
                  <div style={{ ...mono, fontSize: 9, color: "#a855f7", textAlign: "right", fontWeight: 600 }}>
                    {c.toFixed(3)} kg
                  </div>
                </div>
              ))}
            </div>

            {/* System totals */}
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 90px 90px 90px", gap: 6,
              paddingTop: 10, borderTop: `2px solid ${C.border}` }}>
              <div style={{ ...mono, fontSize: 10, color: C.text, fontWeight: 700 }}>TOTAL SYSTEM MASS</div>
              <div style={{ ...mono, fontSize: 13, color: "#ef4444", textAlign: "right", fontWeight: 700 }}>
                {(total_c + 0).toFixed(3)} kg
              </div>
              <div style={{ ...mono, fontSize: 13, color: "#22c55e", textAlign: "right", fontWeight: 700 }}>
                {ep_total_system.toFixed(3)} kg
              </div>
              <div style={{ ...mono, fontSize: 13, color: "#a855f7", textAlign: "right", fontWeight: 700 }}>
                {ion_total_system.toFixed(3)} kg
              </div>
            </div>

            <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 6,
              background: ep_total_system < ion_total_system ? "#22c55e10" : "#a855f710",
              border: `1px solid ${ep_total_system < ion_total_system ? "#22c55e33" : "#a855f733"}` }}>
              <div style={{ ...mono, fontSize: 11, fontWeight: 700,
                color: ep_total_system < ion_total_system ? "#22c55e" : "#a855f7" }}>
                {ep_total_system < ion_total_system
                  ? `EP-ADCS wins by ${(ion_total_system - ep_total_system).toFixed(3)} kg at ${mass} kg satellite`
                  : `Ion wins by ${(ep_total_system - ion_total_system).toFixed(3)} kg at ${mass} kg satellite`}
              </div>
              <div style={{ ...mono, fontSize: 9, color: C.muted, marginTop: 4 }}>
                {ep_total_system < ion_total_system
                  ? `Ion's 150W solar panel requirement (${ion_hw_solar.toFixed(2)} kg) outweighs its propellant saving below ~${ion_crossover} kg.`
                  : `Above ~${ion_crossover} kg, ion propellant saving outweighs the power hardware penalty.`}
              </div>
            </div>
          </Card>

          {/* Ion disqualifiers */}
          <Card color="#ef4444">
            <Label color="#ef4444">Ion Thruster Disqualifiers — Beyond Propellant Mass</Label>
            {[
              ["Plume contamination", "Xenon ions at 20–50 km/s erode solar panels, contaminate optical sensors, degrade mirror coatings 2–15%/yr"],
              ["Jitter (reaction wheels)", "Ion missions still need RWs for attitude → 700–1200 μrad jitter → 360m image smear at 450km"],
              ["Warm-up time", "15 min plasma equilibration before each burn → cannot respond to real-time drag events"],
              ["Minimum power floor", "150W always-on during operation → difficult for <50kg satellites with constrained power budgets"],
              ["No differential drag", "Cannot use Cd modulation for phasing or deorbit — always needs propellant for orbital manoeuvres"],
            ].map(([title, body]) => (
              <div key={title} style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ ...mono, fontSize: 9, color: "#ef4444", marginBottom: 2 }}>✗ {title}</div>
                <div style={{ ...mono, fontSize: 9, color: C.muted }}>{body}</div>
              </div>
            ))}
          </Card>
        </>
      )}

      {/* ══ MISSION IMPACT ══ */}
      {activeTab === "impact" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
            <Stat label="Life extension (same prop)"
              value={total_e_full > 0 ? (total_c / total_e_full * years).toFixed(1) : "∞"} unit="yr"
              color="#0e9cc0"
              sub={`+${total_e_full > 0 ? ((total_c / total_e_full - 1) * years).toFixed(1) : "∞"} yr gain`} />
            <Stat label="Propellant freed for payload"
              value={saved_kg.toFixed(3)} unit="kg" color="#22c55e" />
            <Stat label="% of satellite mass freed"
              value={(saved_kg / mass * 100).toFixed(1)} unit="%" color="#f59e0b" />
          </div>

          <Card color="#a855f7">
            <Label color="#a855f7">EP-ADCS Zero-Propellant Operations</Label>
            {[
              ["In-track Overshoot Elimination",     "EMMS absorbs retro-burn momentum — bang-bang waste → 0"],
              ["Oscillatory Perturbation Rejection",  "EMMS wideband continuous absorption — SRP, J2, tides all handled"],
              ["Reaction Wheel Desaturation",         "RWs replaced by EMMS — desaturation burns permanently eliminated"],
              ["Day-Night Density Synchronisation",   "Fires on denser night side — 8% additional propellant saving"],
              ["Constellation Phasing",               "Cd modulation (face-on Cd≈3.0 / edge-on Cd≈1.5) — any angle"],
              ["EOL Deorbit",                         "Sustained low-Cd drag lowers apogee — passive atmospheric re-entry"],
            ].map(([title, body]) => (
              <div key={title} style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ ...mono, fontSize: 9, color: "#22c55e", marginBottom: 2 }}>✓ {title}</div>
                <div style={{ ...mono, fontSize: 9, color: C.muted }}>{body}</div>
              </div>
            ))}
          </Card>

          <Card color="#22c55e">
            <Label>Propellant Still Needed by EP-ADCS</Label>
            {[
              ["Station Keeping — base drag ΔV", `${sk_e.toFixed(4)} kg`,
               `Drag ${(Fd * 1e6).toFixed(1)} μN is real — thruster fires at Isp=${ispEP}s`],
              ["Residual η lag (2%)", `${(sk_e * 0.02).toFixed(4)} kg`,
               "EMMS coil needs 20 ms to reach full current — only remaining inefficiency"],
            ].map(([lbl, val, note]) => (
              <div key={lbl} style={{ padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ ...mono, fontSize: 9, color: C.text }}>{lbl}</span>
                  <span style={{ ...mono, fontSize: 11, color: "#22c55e", fontWeight: 700 }}>{val}</span>
                </div>
                <div style={{ ...mono, fontSize: 9, color: C.muted, marginTop: 2 }}>{note}</div>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
